import { parseDigit, templateFormatter, templateParser } from "input-format";
import ReactInput from "input-format/react";
import { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import DepartmentBadge from "../../components/deparments/DepartmentBadge";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import UpdateClientTeamsModal from "../../components/client/UpdateClientTeamsModal";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { TAX_NUMBER_REGEX } from "../../helpers/taxNumberRegex";
import { createClient } from "../../services/createClient";

const getDepartmentName = (team) => {
    if (typeof team?.department === "string") return team.department;

    return (
        team?.departmentName ??
        team?.deparmentName ??
        team?.department?.name ??
        team?.department?.departmentName ??
        "Unknown"
    );
};

const getDepartmentColorHex = (team) =>
    team?.colorHex ?? team?.department?.colorHex ?? team?.departmentColorHex ?? null;

const getTeamMembers = (team) => {
    const members =
        team?.members ??
        team?.teamMembers ??
        team?.employees ??
        team?.teamEmployees ??
        [];

    return Array.isArray(members) ? members : [];
};

const getMemberName = (member) => {
    if (!member) return "";
    if (typeof member === "string") return member;

    const fullName = member.fullName ?? member.name ?? member.employeeName ?? "";
    if (fullName) return fullName;

    return `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();
};

function CreateClientForm() {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openTeamsModal, setOpenTeamsModal] = useState(false);
    const [form, setForm] = useState({
        clientName: "",
        phoneNumber: "",
        taxNumber: "",
        teams: [],
        manager: null,
        address: {
            street: "",
            city: "",
            stateProvince: "",
            postalCode: "",
            country: "United States",
        },
        clientDetail: {
            clientType: "",
            spouseName: "",
            spouseEmail: "",
            spouseTaxNumber: "",
            pointOfContactName: "",
            pointOfContactEmail: "",
            secondaryPointOfContactName: "",
            secondaryPointOfContactEmail: "",
            clientEmail: "",
            language: "English",
            contactMethod: "Email",
        },
    });
    async function create() {
        if (form.clientName === "") {
            toast.error("Name is required");
            return;
        }

        if (form.clientDetail.clientEmail === "") {
            toast.error("Email is required");
            return;
        }

        if (form.clientDetail.clientType === "") {
            toast.error("Client type is required");
            return;
        }

        if (!TAX_NUMBER_REGEX.test(String(form.taxNumber).replace(/\D/g, ""))) {
            toast.error("Tax ID Number has an invalid format");
            return;
        }

        if (form.teams.length === 0) {
            toast.error("At least one team is required");
            return;
        }

        var props = Object.keys(form);

        if (form.clientType == "individual") {
            props = props.filter(
                (prop) =>
                    prop != "pointOfContactName" &&
                    prop != "pointOfContactEmail" &&
                    prop != "secondaryPointOfContactName" &&
                    prop != "secondaryPointOfContactEmail"
            );
        } else {
            props = props.filter(
                (prop) =>
                    prop != "spouseEmail" &&
                    prop != "spouseName" &&
                    prop != "spouseTaxNumber"
            );
        }

        let empty = false;
        console.log(props);
        if (!empty) {
            console.log(form);
            const service = await createClient(form);
            console.log(service);
            if (service.success) {
                toast.success(service.message);
                navigate("/clients");
            } else {
                toast.error(service.message);
            }
        } else {
            toast.error("Please fill out all details.");
        }
    }

    return (
        <section>
            <Toaster />
            <UpdateClientTeamsModal
                open={openTeamsModal}
                setOpen={setOpenTeamsModal}
                mode="create"
                clientName={form.clientName || "New client"}
                selectedTeams={form.teams}
                onApply={(selectedTeams) =>
                    setForm((prev) => ({
                        ...prev,
                        teams: selectedTeams,
                    }))
                }
            />
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-2xl">
                                <Breadcrumbs
                                    home="/clients"
                                    pages={[
                                        {
                                            name: "Create client",
                                            href: `/clients/create`,
                                            current: true,
                                        },
                                    ]}
                                />

                                {/* Page header */}
                                <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                    {/* Left: Title */}
                                    <div className="mb-4 sm:mb-0">
                                        <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                            Create client
                                        </h1>
                                    </div>
                                </div>

                                <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold text-slate-800">
                                            Details
                                        </h2>
                                    </header>

                                    <div className="px-5 py-4 space-y-5">
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Name *
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            clientName:
                                                                e.target.value,
                                                        })
                                                    }
                                                    type="text"
                                                    value={form.title}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Email *
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            clientDetail: {
                                                                ...form.clientDetail,
                                                                clientEmail:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    type="text"
                                                    value={
                                                        form.clientDetail
                                                            ?.clientEmail
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Phone number
                                            </label>
                                            <div className="mt-1">
                                                <ReactInput
                                                    value={form.phoneNumber}
                                                    onChange={(value) =>
                                                        setForm({
                                                            ...form,
                                                            phoneNumber: value,
                                                        })
                                                    }
                                                    format={templateFormatter(
                                                        "(xxx) xxx-xxxx"
                                                    )}
                                                    parse={templateParser(
                                                        "(xxx) xxx-xxxx",
                                                        parseDigit
                                                    )}
                                                    displayType="input"
                                                    type="text"
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="pb-1">
                                            <label
                                                htmlFor="clientType"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Client type *
                                            </label>
                                            <div className="mt-1">
                                                <select
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            clientDetail: {
                                                                ...form.clientDetail,
                                                                clientType:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    value={
                                                        form.clientDetail.clientType
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                >
                                                    <option value="">
                                                        Select client type
                                                    </option>
                                                    <option value="individual">
                                                        Individual
                                                    </option>
                                                    <option value="business">
                                                        Business
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="taxNumber"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Tax ID Number *
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    onChange={(e) => {
                                                        const taxNumber =
                                                            e.target.value
                                                                .replace(/\D/g, "")
                                                                .slice(0, 9);

                                                        setForm((prev) => ({
                                                            ...prev,
                                                            taxNumber,
                                                        }));
                                                    }}
                                                    placeholder="Insert only numbers"
                                                    type="text"
                                                    value={formatTaxNumber({
                                                        clientType:
                                                            form?.clientDetail
                                                                ?.clientType,
                                                        taxNumber:
                                                            form?.taxNumber,
                                                    })}
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="pb-1">
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Language
                                            </label>
                                            <div className="mt-1">
                                                <select
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            clientDetail: {
                                                                ...form.clientDetail,
                                                                language:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    value={
                                                        form.clientDetail.language
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                >
                                                    <option value="English">
                                                        English
                                                    </option>
                                                    <option value="Spanish">
                                                        Spanish
                                                    </option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="pb-1">
                                            <label
                                                htmlFor="contactMethod"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Preferred method of contact
                                            </label>
                                            <div className="mt-1">
                                                <select
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            clientDetail: {
                                                                ...form.clientDetail,
                                                                contactMethod:
                                                                    e.target.value,
                                                            },
                                                        })
                                                    }
                                                    value={
                                                        form.clientDetail
                                                            .contactMethod
                                                    }
                                                    className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                >
                                                    <option value="Email">
                                                        Email
                                                    </option>
                                                    <option value="Text">
                                                        Text
                                                    </option>
                                                    <option value="Phone Call">
                                                        Phone Call
                                                    </option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="text-sm font-bold text-slate-800">
                                                            Teams *
                                                        </h3>
                                                        <span className="text-xs font-medium text-slate-500">
                                                            {form.teams.length}
                                                        </span>
                                                    </div>
                                                    <p className="mt-1 text-xs text-slate-500">
                                                        Select at least one team. Only
                                                        one team per department can be
                                                        assigned.
                                                    </p>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setOpenTeamsModal(true)
                                                    }
                                                    className="btn bg-teal-500 text-white hover:bg-teal-600"
                                                >
                                                    {form.teams.length
                                                        ? "Edit teams"
                                                        : "Select teams"}
                                                </button>
                                            </div>

                                            {form.teams.length ? (
                                                <ul className="mt-3 space-y-2">
                                                    {form.teams.map((team) => (
                                                        <li
                                                            key={
                                                                team?.id ??
                                                                team?.teamId ??
                                                                `${getDepartmentName(
                                                                    team
                                                                )}-${team?.name}`
                                                            }
                                                            className="rounded-lg bg-slate-50 p-2"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <DepartmentBadge
                                                                    name={getDepartmentName(
                                                                        team
                                                                    )}
                                                                    colorHex={getDepartmentColorHex(
                                                                        team
                                                                    )}
                                                                />
                                                                <span className="min-w-0 truncate text-sm font-medium text-slate-800">
                                                                    {team.name}
                                                                </span>
                                                            </div>
                                                            <CreateTeamMembersPreview
                                                                members={getTeamMembers(
                                                                    team
                                                                )}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-500">
                                                    No teams selected.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="relative w-full mt-6 bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold capitalize text-slate-800">
                                            {form.clientDetail?.clientType ==
                                            "individual"
                                                ? "Spouse"
                                                : "Business"}
                                        </h2>
                                    </header>

                                    {form.clientDetail?.clientType ==
                                    "individual" ? (
                                        <div>
                                            <div className="px-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    {`Spouse Name`}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    spouseName:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.spouseName
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="px-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Spouse Tax ID Number
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    spouseTaxNumber:
                                                                        e.target.value
                                                                            .replace(
                                                                                /\D/g,
                                                                                ""
                                                                            )
                                                                            .slice(
                                                                                0,
                                                                                9
                                                                            ),
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            formatTaxNumber({
                                                                clientType:
                                                                    "INDIVIDUAL",
                                                                taxNumber:
                                                                    form
                                                                        .clientDetail
                                                                        ?.spouseTaxNumber,
                                                            })
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="px-5 pb-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Spouse Email
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    spouseEmail:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.spouseEmail
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="px-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Main point of contact
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    pointOfContactName:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.pointOfContactName
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="px-5 pb-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Email of main point of contact
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    pointOfContactEmail:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.pointOfContactEmail
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="px-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Name of secondary point of
                                                    contact
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    secondaryPointOfContactName:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.secondaryPointOfContactName
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="px-5 pb-5 mt-5">
                                                <label
                                                    htmlFor="email"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Email of secondary point of
                                                    contact
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        onChange={(e) =>
                                                            setForm({
                                                                ...form,
                                                                clientDetail: {
                                                                    ...form.clientDetail,
                                                                    secondaryPointOfContactEmail:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            })
                                                        }
                                                        type="text"
                                                        value={
                                                            form.clientDetail
                                                                ?.secondaryPointOfContactEmail
                                                        }
                                                        className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="relative w-full mt-6 bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold text-slate-800">
                                            Address
                                        </h2>
                                    </header>

                                    <div className="px-5 mt-5">
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            Street address
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: {
                                                            ...form.address,
                                                            street: e.target.value,
                                                        },
                                                    })
                                                }
                                                type="text"
                                                value={form.address.street}
                                                className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="px-5">
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            City
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: {
                                                            ...form.address,
                                                            city: e.target.value,
                                                        },
                                                    })
                                                }
                                                type="text"
                                                value={form.address.city}
                                                className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="px-5">
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            State/Province
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: {
                                                            ...form.address,
                                                            stateProvince:
                                                                e.target.value,
                                                        },
                                                    })
                                                }
                                                type="text"
                                                value={form.address.stateProvince}
                                                className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="px-5">
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            Postal Code
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: {
                                                            ...form.address,
                                                            postalCode:
                                                                e.target.value,
                                                        },
                                                    })
                                                }
                                                type="text"
                                                value={form.address.postalCode}
                                                className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="px-5 pb-3">
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-700"
                                        >
                                            Country
                                        </label>
                                        <div className="mt-1 mb-2">
                                            <select
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: {
                                                            ...form.address,
                                                            country: e.target.value,
                                                        },
                                                    })
                                                }
                                                value={form.address.country}
                                                className="w-full p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                            >
                                                <option value="United States">
                                                    United States
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end w-full">
                                    <button
                                        onClick={() => create()}
                                        type="submit"
                                        className="z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0"
                                    >
                                        Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    );
}

function CreateTeamMembersPreview({ members }) {
    const list = Array.isArray(members) ? members : [];

    if (!list.length) {
        return (
            <p className="mt-2 ml-1 text-xs text-slate-500">
                No team members assigned
            </p>
        );
    }

    return (
        <div className="mt-2 ml-1 space-y-1">
            {list.slice(0, 5).map((member) => {
                const name = getMemberName(member) || "Unnamed member";

                return (
                    <div
                        key={member?.employeeId ?? member?.id ?? name}
                        className="truncate text-xs text-slate-600"
                        title={name}
                    >
                        {name}
                    </div>
                );
            })}
            {list.length > 5 ? (
                <p className="text-xs text-slate-500">+{list.length - 5} more</p>
            ) : null}
        </div>
    );
}

export default CreateClientForm;
